/**
 * Éléments d'interface du HUD de jeu : médaillon d'icône, étoiles de
 * réputation, onglets, ligne de quota (utilisé / capacité), touche
 * clavier, puces de niveau. Complètent le design system (components.tsx).
 */
import type { ComponentChildren } from 'preact';
import { levelColor, levelLabel } from './labels';
import { n0 } from './format';
import { PgStar, type AnyIcon } from './icons';

export type MedalTone = 'gold' | 'glacier' | 'pine' | 'ember' | 'alert' | 'night' | 'wood' | 'level';

/** Pastille carrée d'un pictogramme ou d'une icône (ressources, installations, fiches). */
export function Medal(props: { icon: AnyIcon; tone?: MedalTone; level?: number | null; size?: number }) {
  const Icon = props.icon;
  const size = props.size ?? 30;
  const style: Record<string, string> = { width: `${size}px`, height: `${size}px` };
  if (props.tone === 'level' || props.level !== undefined) style['--medal'] = levelColor(props.level ?? 0);
  return (
    <span class={`g-medal g-medal-${props.tone ?? 'glacier'}`} style={style}>
      <Icon size={Math.round(size * 0.6)} />
    </span>
  );
}

/** Note sur 5 étoiles (demi-étoiles comprises). */
export function Stars(props: { value: number; title?: string }) {
  const v = Math.max(0, Math.min(5, props.value));
  return (
    <span class="g-stars" title={props.title} aria-label={`${v.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} étoiles sur 5`}>
      {[0, 1, 2, 3, 4].map((i) => {
        const fill = Math.max(0, Math.min(1, v - i));
        return (
          <span class="g-star" key={i}>
            <PgStar size={12} class="g-star-empty" />
            <span class="g-star-fill" style={{ width: `${fill * 100}%` }}>
              <PgStar size={12} />
            </span>
          </span>
        );
      })}
    </span>
  );
}

export function Tabs<T extends string>(props: { value: T; options: { id: T; label: string; badge?: string | number }[]; onChange: (v: T) => void; small?: boolean }) {
  return (
    <div class={`g-tabs${props.small ? ' g-tabs-sm' : ''}`} role="tablist">
      {props.options.map((o) => (
        <button key={o.id} role="tab" aria-selected={props.value === o.id} class={props.value === o.id ? 'on' : ''} onClick={() => props.onChange(o.id)}>
          {o.label}
          {o.badge !== undefined && o.badge !== '' && o.badge !== 0 && <span class="g-tab-badge">{o.badge}</span>}
        </button>
      ))}
    </div>
  );
}

/** Ligne de quota : libellé, valeur « utilisé / capacité », barre colorée. */
export function QuotaRow(props: { label: string; used: number; cap: number; unit?: string; level?: number; hint?: string; invert?: boolean }) {
  const ratio = props.cap > 0 ? props.used / props.cap : 0;
  const level = props.level ?? (ratio >= 1 ? 3 : ratio >= 0.9 ? 2 : ratio >= 0.75 ? 1 : 0);
  return (
    <div class="g-quota" title={props.hint}>
      <div class="g-quota-head">
        <span>{props.label}</span>
        <span class="num">
          {n0(props.used)}
          <small>
            {' '}
            / {n0(props.cap)}
            {props.unit ? ` ${props.unit}` : ''}
          </small>
        </span>
      </div>
      <span class="g-lbar g-lbar-wide">
        <span style={{ width: `${Math.max(2, Math.min(1, ratio) * 100)}%`, background: levelColor(level) }} />
      </span>
    </div>
  );
}

/** Valeur simple sur une ligne : libellé à gauche, valeur à droite. */
export function Fact(props: { label: string; value: ComponentChildren; tone?: 'ok' | 'warn' | 'danger'; title?: string }) {
  return (
    <div class={`g-fact${props.tone ? ` g-fact-${props.tone}` : ''}`} title={props.title}>
      <span>{props.label}</span>
      <span class="num">{props.value}</span>
    </div>
  );
}

/** Puce de niveau de congestion (couleur et libellé). */
export function LevelChip(props: { level: number | null; label?: string }) {
  return (
    <span class="g-lchip" style={{ '--chip': levelColor(props.level) } as Record<string, string>}>
      <i />
      {props.label ?? levelLabel(props.level)}
    </span>
  );
}

/** Légende des niveaux (rapports). */
export function LevelLegend() {
  return (
    <div class="g-legend">
      {[0, 1, 2, 3, -1].map((l) => (
        <LevelChip key={l} level={l} />
      ))}
    </div>
  );
}

export function Kbd(props: { children: ComponentChildren }) {
  return <kbd class="g-kbd">{props.children}</kbd>;
}
